'use client';
/**
 * VOICE COMMAND REGISTRY (Boss 2026-07-13 'a perfect way of controlling beast control room with
 * my voice — anything: mute, minimize, sessions').
 *
 * Any element — a global control or an in-surface button — registers a voice command on mount and
 * unregisters on unmount. The live list of registered commands is injected into the voice model's
 * prompt (voiceCommandsCatalog), so Fable maps Boss's natural phrasing to a REAL command id and
 * emits {{DO:run <id>}}. BeastMode executes it via runVoiceCommand — one deterministic path for
 * every action in the room.
 *
 * Navigation (open/back/home/scroll) stays in Shell; this registry covers ACTIONS (mute, minimize,
 * sessions, and any surface button that opts in with useVoiceCommand).
 */
import { useEffect } from 'react';

export type VoiceCommand = {
  id: string;              // unique, stable — what the model emits: {{DO:run <id>}}
  phrases: string[];       // natural phrasings, shown to the model so it can map intent → id
  hint?: string;           // one-line description of what it does
  scope?: string;          // 'global' | a surface id — for the model's context (optional)
  run: (arg?: string) => void;
};

const registry = new Map<string, VoiceCommand>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => { try { l(); } catch {} });

/** Register a command. Returns an unregister fn (use as an effect cleanup). */
export function registerVoiceCommand(cmd: VoiceCommand): () => void {
  registry.set(cmd.id, cmd);
  notify();
  return () => { if (registry.get(cmd.id) === cmd) { registry.delete(cmd.id); notify(); } };
}

export function listVoiceCommands(): VoiceCommand[] { return [...registry.values()]; }

/** Execute a command by exact id, else by exact phrase match, else fuzzy phrase contains. */
export function runVoiceCommand(id: string, arg?: string): boolean {
  const direct = registry.get(id);
  if (direct) { direct.run(arg); return true; }
  const q = (id || '').toLowerCase().trim();
  if (!q) return false;
  for (const c of registry.values()) {
    if (c.phrases.some(p => p.toLowerCase() === q)) { c.run(arg); return true; }
  }
  // fuzzy: id words all present in a phrase
  const qw = q.split(/\s+/).filter(w => w.length > 2);
  let best: VoiceCommand | null = null, score = 0;
  for (const c of registry.values()) {
    const hay = `${c.id} ${c.phrases.join(' ')}`.toLowerCase();
    const s = qw.filter(w => hay.includes(w)).length;
    if (s > score) { score = s; best = c; }
  }
  if (best && score >= Math.max(1, qw.length - 1)) { best.run(arg); return true; }
  return false;
}

export function onVoiceCommandsChange(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/**
 * Execute every {{DO:...}} control token found in a reply buffer.
 * SHARED EXECUTOR (2026-07-21, Boss typed "Open TORE session and pop it out in the focus mode" —
 * Fable emitted {{DO:run session.switch tore.ca}} and NOTHING happened): the executor lived only
 * inside BeastMode's voice-delta stream handler, so tokens in a TYPED turn's reply were never
 * executed — the model kept "doing" things that never happened. Now BeastMode calls this
 * mid-stream (passing its own per-turn dedup set) and useChat calls it once when a typed turn
 * finishes. Navigation intents dispatch beast:voice-command (the Shell listens); {{DO:run}}
 * actions execute directly through the registry.
 */
export function executeDoTokens(buffer: string, executed?: Set<string>): void {
  const seen = executed ?? new Set<string>();
  const re = /\{\{DO:([^}]+)\}\}/g; let m: RegExpExecArray | null;
  while ((m = re.exec(buffer))) {
    if (seen.has(m[0])) continue;
    seen.add(m[0]);
    const body = m[1].trim();
    const runM = body.match(/^run\s+(\S+)(?:\s+(.+))?$/i);
    if (runM) { runVoiceCommand(runM[1].trim(), runM[2]?.trim()); continue; }
    let cmd: { intent: 'back' | 'home' | 'open' | 'scroll' | 'sort'; arg?: string } | null = null;
    if (/^back$/i.test(body)) cmd = { intent: 'back' };
    else if (/^home$/i.test(body)) cmd = { intent: 'home' };
    else {
      const ms = body.match(/^scroll\s+(left|right|up|down)$/i);
      const so = body.match(/^sort\s+(.+)$/i);
      const mm = body.match(/^open\s+(.+)$/i);
      if (ms) cmd = { intent: 'scroll', arg: ms[1].toLowerCase() };
      else if (so) cmd = { intent: 'sort', arg: so[1].trim().toLowerCase() };
      else if (mm) cmd = { intent: 'open', arg: mm[1].trim() };
    }
    if (cmd) window.dispatchEvent(new CustomEvent('beast:voice-command', { detail: cmd }));
  }
}

/** Compact command list for the voice model's prompt. */
export function voiceCommandsCatalog(): string {
  const cmds = listVoiceCommands();
  if (!cmds.length) return '';
  return cmds
    .map(c => `  ${c.id} — ${c.hint || c.phrases[0]} (say: ${c.phrases.slice(0, 3).join(' / ')})`)
    .join('\n');
}

/**
 * Register a voice command for the lifetime of a component. Pass a stable-ish deps array; the
 * command re-registers when deps change so its `run` closure stays fresh.
 */
export function useVoiceCommand(cmd: VoiceCommand, deps: unknown[] = []): void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => registerVoiceCommand(cmd), deps);
}

/** Register many at once for a component's lifetime. */
export function useVoiceCommands(cmds: VoiceCommand[], deps: unknown[] = []): void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const offs = cmds.map(registerVoiceCommand);
    return () => offs.forEach(off => off());
  }, deps);
}
